import { useEffect, useRef, useState } from "react";

/**
 * Thin reading-progress bar pinned to the top of the viewport while a Learn
 * article is on screen. Tracks scroll position from the start of the article
 * element to the point where its bottom passes the viewport bottom, so the
 * bar fills as the reader actually finishes the content (not the page chrome).
 *
 * Sits just below the global RouteProgressBar (z-40 vs z-50) so route
 * transitions still take visual priority.
 */
export function ArticleScrollProgress({
  targetSelector = "article",
}: {
  targetSelector?: string;
}) {
  const [progress, setProgress] = useState(0);
  const frame = useRef<number | null>(null);

  useEffect(() => {
    const target = document.querySelector<HTMLElement>(targetSelector);
    if (!target) return;

    const compute = () => {
      const rect = target.getBoundingClientRect();
      const viewportH = window.innerHeight;
      // Total scrollable distance across the article element.
      const total = rect.height - viewportH;
      if (total <= 0) {
        setProgress(rect.top <= 0 ? 1 : 0);
        return;
      }
      // How far we've scrolled past the top of the article.
      const scrolled = -rect.top;
      const ratio = Math.max(0, Math.min(1, scrolled / total));
      setProgress(ratio);
    };

    const onScroll = () => {
      if (frame.current !== null) return;
      frame.current = window.requestAnimationFrame(() => {
        frame.current = null;
        compute();
      });
    };

    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame.current !== null) {
        window.cancelAnimationFrame(frame.current);
        frame.current = null;
      }
    };
  }, [targetSelector]);

  const pct = Math.round(progress * 100);

  return (
    <div
      role="progressbar"
      aria-label="Article reading progress"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      className="pointer-events-none fixed inset-x-0 top-0 z-40 h-[3px]"
    >
      <div
        className="h-full origin-left bg-gradient-gold shadow-gold transition-transform duration-150 ease-out"
        style={{ transform: `scaleX(${progress})`, width: "100%" }}
      />
    </div>
  );
}
